import { get } from 'svelte/store';
import type { Phase } from '@banger/shared';
import { view } from './view';

/**
 * Stale-client guard: after a deploy, an open tab still runs the old bundle against the new
 * worker. The build writes its id into the bundle (__BUILD_ID__) and into /version.json; when
 * the deployed id differs, reload once per id (sessionStorage stops a reload loop).
 */
export const BUILD_ID: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';

const RELOADED_KEY = 'banger.reloadedFor';

/** True when `deployed` is a real build id, differs from the running one, and wasn't reloaded for yet. */
export function shouldReload(running: string, deployed: unknown, reloadedFor: string | null): deployed is string {
  return typeof deployed === 'string' && deployed !== '' && deployed !== running && deployed !== reloadedFor;
}

/**
 * Phones reload any time (they rejoin at once). The TV plays the music, so it waits for the
 * lobby or finale; with no room connected there is nothing to interrupt.
 */
export function reloadAllowed(current: { role: 'display' | 'player'; phase: Phase } | null): boolean {
  return !current || current.role !== 'display' || current.phase === 'lobby' || current.phase === 'finale';
}

interface VersionDeps {
  running: string;
  fetch: (url: string, init: RequestInit) => Promise<Pick<Response, 'ok' | 'json'>>;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  /** Whether reloading now is fine (default yes); if not, `defer` gets the new id. */
  allowed?: () => boolean;
  defer?: (deployed: string) => void;
}

/** Compare with the deployed /version.json; true if it reloaded. Never throws. */
export async function checkVersion(deps: VersionDeps): Promise<boolean> {
  try {
    const res = await deps.fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return false;
    const deployed = ((await res.json()) as { build?: unknown } | null)?.build;
    if (!shouldReload(deps.running, deployed, deps.storage.getItem(RELOADED_KEY))) return false;
    if (deps.allowed && !deps.allowed()) {
      deps.defer?.(deployed);
      return false;
    }
    // Without a remembered id there is no loop guard, so don't reload at all.
    deps.storage.setItem(RELOADED_KEY, deployed);
    deps.reload();
    return true;
  } catch {
    return false;
  }
}

let checking = false;
/** A newer build the TV is waiting to reload into once the match is over. */
let pending = false;
let watching = false;

function deps(): VersionDeps {
  return {
    running: BUILD_ID,
    fetch: (url, init) => fetch(url, init),
    storage: sessionStorage,
    reload: () => location.reload(),
    allowed: () => reloadAllowed(get(view)),
    defer: () => {
      pending = true;
      watchForSafeMoment();
    },
  };
}

/** Re-check when the view reaches a phase where reloading is allowed. */
function watchForSafeMoment(): void {
  if (watching) return;
  watching = true;
  view.subscribe((v) => {
    if (pending && !checking && reloadAllowed(v)) {
      pending = false;
      checkForNewBuild();
    }
  });
}

/** Production only (dev and tests have no /version.json): check, one request at a time. */
export function checkForNewBuild(): void {
  if (!import.meta.env.PROD || checking || typeof window === 'undefined') return;
  checking = true;
  try {
    // deps() touches sessionStorage, which throws SecurityError when storage is blocked.
    void checkVersion(deps()).finally(() => (checking = false));
  } catch {
    checking = false;
  }
}
